/**
 * @file NotificationSystem.js
 * @description Sistema de notificaciones toast y diálogos de confirmación
 * @module components/NotificationSystem
 * 
 * Componentes principales:
 * 1. NotificationItem: Notificación toast individual con animaciones
 * 2. NotificationContainer: Contenedor de todas las notificaciones activas
 * 3. ConfirmDialog: Diálogo modal de confirmación con estados de carga
 * 
 * Características del sistema:
 * - Notificaciones toast no intrusivas en esquina superior derecha
 * - 4 tipos de notificación: success, error, warning, info
 * - Animaciones suaves de entrada/salida
 * - Auto-dismiss configurable
 * - Dismiss manual con botón X
 * - Diálogos de confirmación con tipos visuales (danger, warning, info)
 * - Estados de carga en diálogos (spinner + botones deshabilitados)
 * - Z-index alto (9999) para estar sobre todo
 * - Diseño responsive y accesible
 * 
 * Tipos de notificación:
 * - success: Verde, para operaciones exitosas
 * - error: Rojo, para errores y fallos
 * - warning: Amarillo, para advertencias
 * - info: Azul, para información general
 * 
 * Tipos de confirmación:
 * - danger: Rojo, para acciones destructivas (eliminar, etc.)
 * - warning: Amarillo, para acciones que requieren precaución
 * - info: Azul, para confirmaciones informativas
 */

import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, XCircle, Info, Loader2 } from 'lucide-react';

/**
 * Componente NotificationItem - Notificación toast individual
 * 
 * Renderiza una notificación tipo toast con:
 * - Animación de entrada desde la derecha
 * - Borde izquierdo coloreado según tipo
 * - Ícono correspondiente al tipo
 * - Título opcional y mensaje
 * - Botón de cierre
 * - Animación de salida cuando se cierra
 * 
 * Animaciones:
 * - Entrada (0-50ms): translate-x-full → translate-x-0, opacity 0 → 1
 * - Salida (200ms): translate-x-0 → translate-x-full, opacity 1 → 0
 * 
 * @component
 * @param {Object} props
 * @param {Object} props.notification - Objeto de notificación
 * @param {string} props.notification.id - ID único de la notificación
 * @param {('success'|'error'|'warning'|'info')} props.notification.type - Tipo de notificación
 * @param {string} [props.notification.title] - Título opcional de la notificación
 * @param {string} props.notification.message - Mensaje de la notificación
 * @param {Function} props.onRemove - Callback para remover la notificación
 * @returns {JSX.Element} Notificación toast animada
 */
export const NotificationItem = ({ notification, onRemove }) => {
  /**
   * Estado de visibilidad para animación de entrada
   * Inicia en false y se activa después de 50ms
   */
  const [isVisible, setIsVisible] = useState(false);
  
  /**
   * Estado de remoción para animación de salida
   * Se activa al hacer click en X
   */
  const [isRemoving, setIsRemoving] = useState(false);

  /**
   * Effect: Animar entrada de la notificación
   * Espera 50ms antes de hacerla visible para la animación
   */
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  /**
   * Handler para remover la notificación
   * 
   * Flujo:
   * 1. Activar estado de remoción (inicia animación de salida)
   * 2. Esperar 200ms para que termine la animación
   * 3. Llamar al callback onRemove para eliminar del estado
   */
  const handleRemove = () => {
    setIsRemoving(true);
    setTimeout(() => onRemove(notification.id), 200);
  };

  /**
   * Obtiene el ícono correspondiente al tipo de notificación
   * 
   * @returns {JSX.Element} Ícono de Lucide React con color apropiado
   */
  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      default:
        return <Info className="w-5 h-5 text-blue-600" />;
    }
  };

  /**
   * Obtiene las clases de estilo según el tipo
   * 
   * Incluye:
   * - Base: fondo blanco, borde gris
   * - Borde izquierdo coloreado según tipo (4px de grosor)
   * 
   * @returns {string} String de clases Tailwind
   */
  const getStyles = () => {
    const baseStyles = "bg-white rounded-lg border border-gray-200";
    const borderLeft = {
      success: 'border-l-4 border-l-green-500',
      error: 'border-l-4 border-l-red-500', 
      warning: 'border-l-4 border-l-yellow-500',
      info: 'border-l-4 border-l-blue-500'
    };
    
    return `${baseStyles} ${borderLeft[notification.type] || borderLeft.info}`;
  };

  return (
    <div
      className={`
        ${getStyles()}
        transform transition-all duration-200 ease-out
        ${isVisible && !isRemoving 
          ? 'translate-x-0 opacity-100 scale-100' 
          : 'translate-x-full opacity-0 scale-95'
        }
        w-96 pointer-events-auto
      `}
    >
      <div className="p-4">
        <div className="flex items-start">
          {/* Ícono */}
          <div className="flex-shrink-0 pt-0.5">
            {getIcon()}
          </div>
          
          {/* Contenido: Título y Mensaje */}
          <div className="ml-3 flex-1 min-w-0">
            {notification.title && (
              <p className="text-sm font-semibold text-gray-900 leading-tight">
                {notification.title}
              </p>
            )}
            <p className={`text-sm text-gray-700 leading-relaxed ${notification.title ? 'mt-1' : ''}`}>
              {notification.message}
            </p>
          </div>
          
          {/* Botón de cierre */}
          <div className="ml-4 flex-shrink-0">
            <button
              onClick={handleRemove}
              className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition-colors p-1 rounded hover:bg-gray-100"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Componente NotificationContainer - Contenedor de notificaciones
 * 
 * Posicionado en esquina superior derecha con z-index 9999.
 * Renderiza todas las notificaciones activas en una pila vertical.
 * 
 * Características:
 * - Posición fija en top-right
 * - pointer-events-none en el contenedor para no bloquear clicks
 * - pointer-events-auto en cada notificación individual
 * - Stack vertical con espacio entre notificaciones (space-y-3)
 * - z-index máximo (9999) para estar sobre todo
 * 
 * @component
 * @param {Object} props
 * @param {Array<Object>} props.notifications - Array de notificaciones activas
 * @param {Function} props.onRemove - Callback para remover notificaciones
 * @returns {JSX.Element} Contenedor con stack de notificaciones
 */
export const NotificationContainer = ({ notifications, onRemove }) => {
  return (
    <div className="fixed top-4 right-4 z-[9999] space-y-3 pointer-events-none">
      {notifications.map(notification => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onRemove={onRemove}
        />
      ))}
    </div>
  );
};

/**
 * Componente ConfirmDialog - Diálogo de confirmación modal
 * 
 * Diálogo modal centrado para confirmar acciones críticas.
 * Soporta estados de carga con spinner y botones deshabilitados.
 * 
 * Características:
 * - Overlay oscuro de fondo
 * - Modal centrado con animaciones
 * - 3 tipos visuales: danger (rojo), warning (amarillo), info (azul)
 * - Estado de carga con spinner animado
 * - Botones deshabilitados durante carga
 * - Click fuera del modal para cerrar (si no está cargando)
 * - z-index 9998/9999 para estar sobre todo excepto notificaciones
 * 
 * Estados:
 * - Normal: Muestra ícono según tipo, botones activos
 * - Loading: Muestra spinner, botones deshabilitados, texto de carga
 * 
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen - Si el diálogo está abierto
 * @param {Function} props.onClose - Callback para cerrar el diálogo
 * @param {Function} props.onConfirm - Callback al confirmar la acción
 * @param {string} props.title - Título del diálogo
 * @param {string} props.message - Mensaje explicativo del diálogo
 * @param {('danger'|'warning'|'info')} [props.type='warning'] - Tipo visual del diálogo
 * @param {boolean} [props.isLoading=false] - Si está en estado de carga
 * @param {string} [props.loadingMessage='Procesando...'] - Mensaje durante carga
 * @returns {JSX.Element|null} Diálogo modal o null si está cerrado
 */
export const ConfirmDialog = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  type = 'warning',
  isLoading = false,
  loadingMessage = 'Procesando...'
}) => {
  // Guard clause: no renderizar si está cerrado
  if (!isOpen) return null;

  /**
   * Obtiene el ícono correspondiente al tipo o estado
   * 
   * - Si está cargando: spinner azul animado
   * - Si no: ícono según tipo (danger, warning, info)
   * 
   * @returns {JSX.Element} Ícono en círculo coloreado
   */
  const getIcon = () => {
    // Estado de carga: spinner animado
    if (isLoading) {
      return (
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
          <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
        </div>
      );
    }

    // Iconos normales según el tipo
    switch (type) {
      case 'danger':
        return (
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <XCircle className="w-6 h-6 text-red-600" />
          </div>
        );
      case 'warning':
        return (
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
            <AlertCircle className="w-6 h-6 text-yellow-600" />
          </div>
        );
      case 'info':
        return (
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
            <Info className="w-6 h-6 text-blue-600" />
          </div>
        );
      default:
        return (
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
            <AlertCircle className="w-6 h-6 text-yellow-600" />
          </div>
        );
    }
  };

  /**
   * Obtiene colores del botón de confirmación según tipo
   * 
   * @returns {string} Clases Tailwind de color
   */
  const getButtonColors = () => {
    switch (type) {
      case 'danger':
        return 'bg-red-600 hover:bg-red-700 focus:ring-red-500';
      case 'warning':
        return 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500';
      case 'info':
        return 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500';
      default:
        return 'bg-red-600 hover:bg-red-700 focus:ring-red-500';
    }
  };

  /**
   * Obtiene colores del botón durante estado de carga
   * 
   * @returns {string} Clases Tailwind de color (gris deshabilitado)
   */
  const getLoadingButtonColors = () => {
    return 'bg-gray-400 cursor-not-allowed';
  };

  return (
    <>
      {/* Overlay de fondo oscuro */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[9998] transition-opacity"
        onClick={!isLoading ? onClose : undefined} // Solo permitir cerrar si no está cargando
      />
      
      {/* Modal centrado */}
      <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4">
        <div 
          className="bg-white rounded-2xl max-w-md w-full transform transition-all border border-gray-200"
          onClick={(e) => e.stopPropagation()} // Prevenir cierre al hacer click dentro
        >
          <div className="p-6">
            {/* Ícono */}
            <div className="mb-4">
              {getIcon()}
            </div>
            
            {/* Título */}
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              {isLoading ? 'Procesando' : title}
            </h3>
            
            {/* Mensaje */}
            <p className="text-gray-600 text-center mb-6 leading-relaxed">
              {isLoading ? loadingMessage : message}
            </p>
            
            {/* Botones */}
            <div className="flex justify-center gap-3">
              {/* Botón Cancelar */}
              <button
                onClick={onClose}
                disabled={isLoading}
                className={`px-6 py-2.5 rounded-lg transition-colors font-medium ${
                  isLoading 
                    ? 'text-gray-400 bg-gray-100 cursor-not-allowed' 
                    : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                }`}
              >
                Cancelar
              </button>
              
              {/* Botón Confirmar */}
              <button
                onClick={onConfirm}
                disabled={isLoading}
                className={`px-6 py-2.5 text-white rounded-lg transition-colors font-medium flex items-center gap-2 ${
                  isLoading 
                    ? getLoadingButtonColors()
                    : getButtonColors()
                }`}
                >
                {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                {isLoading ? 'Procesando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};